import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import _ from 'lodash';
import TagSelector from 'components/selectors/TagSelector';
import { MockServiceAnalytics } from 'types/rest/mockServiceTypes';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';
import i18n from 'i18n';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { useDispatch } from 'react-redux';
import { reconfigureMockServiceThunk } from 'store/mockServices/mockServiceList/mockServiceListActions';
import axios from 'axios/axios-base';
import { hideToast, showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import {
  fetchAnalyticsInspectionDataDetail,
  patchAnalyticsInspectionData,
} from 'store/analytics/data/analyticsDataActions';
import { TxiViewRequest } from 'types/rest/analyticsTypes';
import { Tag } from 'types/rest/assetCatalogTypes';
import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import { Form } from 'react-bootstrap';
import SimpleLoader from 'components/loaders/SimpleLoader';
import BzmIcon from '../../../../components/icons/BzmIcon';

type SaveLiveRequestModalProps = {
  /** Request used for transaction creation */
  request: TxiViewRequest | null;

  /** Controls show / hide of the modal */
  isModalShown: boolean;

  /** On hide event */
  onHide: () => void;

  /** Virtual Service metadata */
  mockServiceAnalytics: MockServiceAnalytics;

  /** Callback used when txn creation from request finished */
  onTransactionCreated: (request: TxiViewRequest) => void;
};

const SaveLiveRequestModal = (props: SaveLiveRequestModalProps) => {
  const [transactionName, setTransactionName] = useState({
    value: '',
    isInvalid: false,
  });
  const [updateMockService, setUpdateMockService] = useState(false);
  const [assignedTags, setAssignedTags] = useState([] as any[]);
  const [isWorking, setIsWorking] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation('analytics');

  const nameInput = useFormInput(
    transactionName.value,
    (fn: CallbackFn) => {
      setTransactionName({ ...transactionName, value: fn.value as string, isInvalid: !fn.isValid });
    },
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('transactions:editor.txnNameRequired'),
    },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 250,
      invalidMessage: i18n.t('transactions:editor.txnNameMaxLength'),
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage: i18n.t('transactions:editor.txnNameIsInvalid'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      invalidMessage: i18n.t('transactions:editor.txnNameIsInvalid'),
    },
  );

  /**
   * Sends patch which assigns transaction to existing service. If service is running
   * it triggers reconfiguration.
   * @param createdTransactionId
   */
  const reconfigureMockServiceHandler = async (createdTransactionId: number) => {
    if (_.isNil(props.mockServiceAnalytics.meta)) {
      return;
    }

    // Show in progress toast msg
    const infoToast = dispatch(
      showToastNotification(
        ToastType.Info,
        i18n.t('analytics:createTransactionModal.toast.heading'),
        i18n.t('analytics:createTransactionModal.toast.body'),
        0,
      ),
    );

    const patchResponse = (
      await axios
        .patch(
          `/workspaces/{workspaceId}/service-mocks/${props.mockServiceAnalytics.meta.serviceMockId}`,
          { includeIds: [createdTransactionId] },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          },
        )
        .catch()
    ).data.result;

    const mockService = {
      ...props.mockServiceAnalytics.meta,
      id: props.mockServiceAnalytics.meta.serviceMockId,
      isDraft: false,
    };

    // if running, start reconfigure
    if (patchResponse.status && patchResponse.status === 'RUNNING') {
      dispatch(reconfigureMockServiceThunk(mockService));
    }

    //Hide toast before success toast
    await dispatch(hideToast(infoToast));

    // Show toast with augmentation info
    const toast = showToastNotification(
      ToastType.Success,
      '',
      getSuccessToastBody(createdTransactionId, mockService.name),
      6000,
    );
    dispatch(toast);
  };

  /**
   * Gets transaction DSL from inspection data via convert endpoint.
   */
  const getTransactionDsl = async () => {
    if (_.isNil(props.request)) {
      throw Error('Cannot get transaction dsl');
    }

    // Create wiremock dsl and send it to convert endpoint
    // requestBody & responseBody has to be base64 encoded
    const inspectionReqDetails = (await fetchAnalyticsInspectionDataDetail(props.request.id)).data.result;
    const wiremockDsl = [
      {
        request: props.request,
        details: inspectionReqDetails,
      },
    ];

    const file = new File([JSON.stringify(wiremockDsl)], 'wiremockDsl.json', {
      type: 'text/plain',
    });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const transactions = (
        await axios.post('/transactions/convert', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json',
          },
        })
      ).data.result;
      return transactions[0];
    } catch (error) {
      const errorToast = showToastNotification(
        ToastType.Danger,
        'Cannot create transaction',
        _.get(error, 'response.data.error', error.toString()),
      );
      dispatch(errorToast);
      setIsWorking(false);
      return null;
    }
  };

  const createTransaction = (serviceId: any, transactionDsl: any) => {
    return axios.post(
      `/workspaces/{workspaceId}/transactions`,
      {
        transactions: [
          {
            type: 'HTTP', //TODO bring type by parameter??
            name: transactionName.value,
            dsl: transactionDsl,
          },
        ],
      },
      {
        params: {
          serviceId: serviceId,
          tags: assignedTags.length > 0 ? getAssignedTagsAsString() : undefined,
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
    );
  };

  /**
   * Set liveTransactionCreated flag of the request in analytics.
   */
  const setLiveTransactionCreated = () => {
    if (_.isNil(props.request)) {
      throw new Error('Cannot patch analytics request transaction. Missing request data');
    }

    const patchBody = {
      liveTransactionCreated: true,
    };

    return patchAnalyticsInspectionData(props.request.id, patchBody);
  };

  /**
   * Maps list of tags to list of strings.
   */
  const getAssignedTagsAsString = () => {
    return assignedTags.map((tag: Tag) => tag.name).toString();
  };

  /**
   * Builds body of success toast msg. If mockService name was provided, it means it was
   * augmented with new transaction and msg has to be included in the body.
   * @param txnId
   */
  const getSuccessToastBody = (txnId: any, mockServiceName?: string) => {
    return (
      <>
        <span
          className="mr-2 ml-2"
          dangerouslySetInnerHTML={{
            __html: t('createTransactionModal.transactionCreationSuccess', {
              name: transactionName.value,
            }),
          }}
        ></span>

        <>
          (id: <b>{txnId}</b>)
        </>

        {/* Virtual Service Augmentation */}
        {_.isNil(mockServiceName) ? (
          '.'
        ) : (
          <span
            dangerouslySetInnerHTML={{
              __html: t('createTransactionModal.transactionCreationAugmentation', {
                mockServiceName: mockServiceName,
              }),
            }}
          ></span>
        )}
        {/* ./Virtual Service Augmentation */}
        {/* Assigned Tags */}
        <span className="ml-2">
          {/* Tags Plurar Msg */}
          {assignedTags.length >= 2 && (
            <span
              dangerouslySetInnerHTML={{
                __html: t('createTransactionModal.transactionCreationTagsApplied', {
                  tagNames: getAssignedTagsAsString(),
                }),
              }}
            ></span>
          )}
          {/* Tags singular Msg */}
          {assignedTags.length === 1 && (
            <span
              dangerouslySetInnerHTML={{
                __html: t('createTransactionModal.transactionCreationTagApplied', {
                  tagName: getAssignedTagsAsString(),
                }),
              }}
            ></span>
          )}
        </span>
        {/* ./Assigned Tags */}
      </>
    );
  };
  /**
   * Reset inputs to default values on close event.
   */
  const hideModalHandler = () => {
    //Reset input fields to default state
    setTransactionName({ ...transactionName, isInvalid: false, value: '' });
    setAssignedTags([]);
    setUpdateMockService(false);
    nameInput.resetValue('');
    nameInput.overrideValidation({
      isValid: true,
      invalidMessage: '',
    });
    setIsWorking(false);

    //Hide modal
    props.onHide();
  };

  const createTransactionHandler = async () => {
    if (_.isNil(props.mockServiceAnalytics.meta) || _.isNil(props.request)) {
      throw new Error('Cannot create transaction. Missing metadata for the service or request data.');
    }

    await setIsWorking(true);

    try {
      //Convert -> Create new transaction from dsl. It will also validate the name
      const transactionDsl = await getTransactionDsl();

      const serviceId = parseInt(props.mockServiceAnalytics.meta.serviceId);
      const createdTransaction = (await createTransaction(serviceId, transactionDsl)).data.result[0];

      // Patch analytics - txn was created from the request
      await setLiveTransactionCreated();
      props.onTransactionCreated({ ...props.request, liveTransactionCreated: true });

      await setIsWorking(false);
      hideModalHandler();

      //Update Virtual Service if needed
      if (updateMockService === true) {
        await reconfigureMockServiceHandler(createdTransaction.id);
      } else {
        //Show simple success msg (about created txn and list of assigned tags)
        dispatch(showToastNotification(ToastType.Success, '', getSuccessToastBody(createdTransaction.id), 7000));
      }
    } catch (error) {
      setIsWorking(false);
      // Default error notification with msg from the backend
      const toastNotification = showToastNotification(
        ToastType.Danger,
        'Cannot create transaction',
        _.get(error, 'response.data.error', error.toString()),
      );

      //If backend returns 400 & already exists message, do not show error just invalidate txn name form input.
      if (_.has(error, 'response.data.error') && error.response.status === 400) {
        const msg = error.response.data.error;
        if (msg.startsWith(`Transaction ${transactionName.value} already exists`)) {
          nameInput.overrideValidation({
            isValid: false,
            invalidMessage: 'Transaction already exists. New transaction name must be unique.',
          });
          setTransactionName({ ...transactionName, isInvalid: true });
        } else {
          dispatch(toastNotification);
        }
      } else {
        dispatch(toastNotification);
      }
    }
  };

  if (_.isNil(props.request)) {
    return null;
  }

  const isFormValid = () => {
    return !_.isEmpty(transactionName.value) && !transactionName.isInvalid;
  };

  return (
    <Modal show={props.isModalShown} onHide={hideModalHandler} size="sm">
      <Modal.Header closeButton>
        <Modal.Title>{t('createTransactionModal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{t('createTransactionModal.info')}</p>

        <Form.Group controlId="create-txn-name">
          <Form.Label>Transaction Name</Form.Label>
          <BzFormControlWithHook
            autoComplete="off"
            useFormInputHook={nameInput}
            placeholder={t('createTransactionModal.enterTxnName')}
            required={true}
          />
        </Form.Group>

        <Form.Group controlId="qa-input-tags-dropzone">
          <label htmlFor={'react-select-multiple'}>Tags</label>
          <TagSelector selectHandler={tags => setAssignedTags(tags)} />
        </Form.Group>

        <label>{t('createTransactionModal.service')}</label>
        <p>{props.mockServiceAnalytics.meta && props.mockServiceAnalytics.meta.serviceName}</p>

        <div className="custom-control custom-switch custom-switch-lg">
          <input type="checkbox" className="custom-control-input" checked={updateMockService} />

          <label
            onClick={() => setUpdateMockService(!updateMockService)}
            className="custom-control-label"
            htmlFor={'updateMockService-switch'}
          >
            {t('createTransactionModal.updateMockService')}
          </label>
        </div>

        {updateMockService && (
          <p className="text text-warning mt-4 mb-0">
            <BzmIcon variant={'icon-bzm-info'} className={'icon-xs mr-1'} />
            {t('createTransactionModal.updateMockServiceCaption')}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-link" onClick={hideModalHandler}>
          {t('common:actions.cancel')}
        </button>
        <Button
          variant="primary"
          disabled={!isFormValid() || isWorking}
          onClick={createTransactionHandler}
          style={{ width: 60 }}
        >
          {isWorking ? <SimpleLoader showLoadingText={false} /> : t('common:actions.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveLiveRequestModal;
